import React, {Component, Fragment} from 'react'
import PropTypes from 'prop-types'
import {API_HOST} from 'constants'

import LoadingShade from './Common/LoadingShade'
import ChartContainer from './Common/ChartContainer'
import {
  initCandleChart, setChartSymbol, Indis, saveChartUserSettings
} from './Common/chart'

const DEFAULT_VIEW_NAME = 'default'
const DEFAULT_CHART_THEME = 'chart-black-bnb-pro'
const DEFAULT_CHART_LINE_TYPE = 'Candle'

export default // PureComponent because we do not want to rerender if `tickers` is the same.
// https://github.com/facebook/react/blob/d7157651f7b72d9888c0123e191f9b88cd8f41e9/src/renderers/shared/stack/reconciler/ReactCompositeComponent.js#L902-L922
class DefaultChart extends Component {
  static propTypes = {
    lastKlineUpdate: PropTypes.object,
    locale: PropTypes.string,
    minQty: PropTypes.number,
    symbol: PropTypes.string.isRequired,
    quoteAsset: PropTypes.string,
    symbols: PropTypes.array,
    tickSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedView: PropTypes.string,
    fullScreen: PropTypes.bool,
    interval: PropTypes.number,
    intervalName: PropTypes.string,
    lineType: PropTypes.string,
    setDataIsLoading: PropTypes.func,
    curIndicators: PropTypes.object,
    setCurIndicator: PropTypes.func,
    indicatorName: PropTypes.string,
    drawLineType: PropTypes.string,
    dataSetup: PropTypes.number,
    isDataLoading: PropTypes.bool
  }

  static defaultProps = {
    lastKlineUpdate: null,
    locale: 'en',
    minQty: 0.01,
    quoteAsset: '',
    symbols: [],
    tickSize: '0.01',
    selectedView: DEFAULT_VIEW_NAME,
    fullScreen: false,
    interval: 60,
    lineType: DEFAULT_CHART_LINE_TYPE,
    intervalName: '1m',
    setDataIsLoading: () => {},
    setCurIndicator: () => {},
    curIndicators: {},
    indicatorName: '',
    drawLineType: '',
    dataSetup: 0,
    isDataLoading: true
  }

  shouldComponentUpdate ({
    selectedView, symbol, interval, fullScreen
  }) {
    const defaultIsNow = this.props.selectedView === DEFAULT_VIEW_NAME
    const defaultIsNext = selectedView === DEFAULT_VIEW_NAME
    const fullScreenChange = fullScreen !== this.props.fullScreen

    if (fullScreenChange) setTimeout(() => this._chart?.resize(), 0)
    // default is next or we are leaving default (so hide it)
    return (
      fullScreenChange
      || defaultIsNext
      || (defaultIsNow && !defaultIsNext)
      // symbol or interval changed, update chart even if hidden
      || (this._chart && symbol !== this.props.symbol)
      || (this._chart && interval !== this.props.interval)
    )
  }

  componentDidUpdate ({
    symbol: prevSymbol,
    interval: prevInterval,
    lineType: prevLineType,
    indicatorName: preIndicatorName,
    curIndicators: preCurIndicators,
    drawLineType: preDrawLineType,
    dataSetup: prevDataSetup
  }) {
    const {
      symbol,
      quoteAsset,
      selectedView,
      fullScreen,
      interval,
      lineType,
      indicatorName,
      curIndicators,
      intervalName,
      lastKlineUpdate,
      dataSetup
    } = this.props
    // clear data if symbol does not exist
    if (typeof quoteAsset !== 'string') {
      this._chart.clearData()
      return
    }

    if (!this._chart) return

    if (lineType !== prevLineType) {
      this._chart.removeIndicatorByname(['ma', 'ma', 'ma'])
      this._chart.removeIndicatorByname(['avl'])
      this._removeOver(this.props.curIndicators.curOver)

      if (lineType === 'Line') {
        this._chart.setLastColorIndex(0)

        this._chart.addOrUpdateIndicator('avl')
        this.props.setCurIndicator({curOver: 'AVL'})
        if (this.props.pro) saveChartUserSettings(this._chart)
      } else {
        this._chart.setLastColorIndex(1)
        this._chart.addOrUpdateIndicator('ma', 7)
        this._chart.addOrUpdateIndicator('ma', 25)
        this._chart.addOrUpdateIndicator('ma', 99)
        this.props.setCurIndicator({curOver: 'MA'})
        if (this.props.pro) saveChartUserSettings(this._chart)
      }

      if (lineType) {
        this._chart.setMode(lineType)
      }
    }

    if (Object.keys(lastKlineUpdate).length === 0 && interval === prevInterval) return

    if (dataSetup !== prevDataSetup) {
      window.Data && window.Data.setup()
    }

    if (lastKlineUpdate && lastKlineUpdate[intervalName]) {
      const candlesticks = lastKlineUpdate[intervalName]
      const {k: ticks} = candlesticks
      const {
        o: open, h: high, l: low, c: close, v: volume, q: quoteVolume, t: tickTime
      } = ticks
      let bar = [open, high, low, close, volume, quoteVolume]
      bar = bar.map(parseFloat)
      bar.unshift(tickTime)

      if (window.Data && this._chart) window.Data.onBar(bar, interval)
    }

    // resize chart if fullscreen toggled
    if (selectedView === DEFAULT_VIEW_NAME) {
      // TODO: this spams klines updates!
      // this._chart.setResolution(interval);
      this._init()

      // fullscreen toggled?
      if (fullScreen) {
        this._chart.resize()
      }
    }
    if (symbol !== prevSymbol) {
      this.props.setDataIsLoading({key: 'kline', isLoading: true})
    }
    // update the "candlesticks" chart on symbol change
    if (symbol !== prevSymbol && prevSymbol) {
      this._chart.clearData()
      setChartSymbol({...this.props, quoteAsset})
    }

    // interval changed?
    if (interval !== prevInterval) {
      this.props.setDataIsLoading({key: 'kline', isLoading: true})
      this._chart.setResolution(interval)
    }
    if (indicatorName !== preIndicatorName) {
      this._setIndicator(indicatorName)
    }
    if (!curIndicators.curInd) {
      this._removeIndicator(preCurIndicators.curInd)
    }
    if (!curIndicators.curOver) {
      this._removeOver(preCurIndicators.curOver)
    }
    if (this.props.drawLineType === 'clear') {
      this.line.clean()
    } else if (this.props.drawLineType === 'deleteSelected') {
      this.line.deleteLine()
    } else if (this.props.drawLineType !== preDrawLineType) {
      this.line.add(this.props.drawLineType)
      this._chart.setCrossHair(false)
    }
  }

  componentDidMount () {
    // TODO: not a great way to do this
    this.props.setDataIsLoading({key: 'kline', isLoading: true})
    window
    .jQuery(this._chartContainer.current)
    .on('blur', '.icontrol input', () => saveChartUserSettings(this._chart))
    const {locale} = this.props
    if (locale === 'cn') window.localStorage.setItem('lang', 'cn')
    else if (window.localStorage) window.localStorage.setItem('lang', 'en')

    this._init()
  }

  componentWillUnmount () {
    /* this._resizeHandler
        && DetectElementResize.removeResizeListener(this._chartContainer.current,
          this._resizeHandler) */
    window.jQuery(window).off('resize')
    this._chart && this._chart.clearData()
  }

  _init () {
    if (!this._chart) {
      const {
        symbol, quoteAsset, tickSize, minQty, interval, lineType, pro
      } = this.props

      // candlesticks chart
      const containerEl = this._chartContainer.current
      const baseApi = __DEV__ ? '' : API_HOST
      const {
        chart, line, curInd, curOver
      } = initCandleChart({
        symbol,
        quoteAsset,
        themeName: DEFAULT_CHART_THEME,
        tickSize,
        minQty,
        defaultInterval: parseInt(interval, 10),
        defaultLineType: lineType,
        containerEl,
        pro,
        baseApi
      })
      this._chart = chart
      this.line = line
      this.props.setCurIndicator({curOver, curInd})
      const self = this
      this.props.setDataIsLoading({key: 'kline', isLoading: true})
      window.Data.onLoaded(() => {
        self.props.setDataIsLoading({key: 'kline', isLoading: false})
      })

      // call chart.resize() when the element resizes
      window.jQuery(window).resize(() => {
        this._chart && this._chart.resize()
      })
      /* DetectElementResize.addResizeListener(
        this._chartContainer.current, this._resizeHandler = () => {
          this._chart && this._chart.resize()
        }) */

      this._chart.AddRemoveIndicatorCallback(() => {
        if (this.props.pro) saveChartUserSettings(this._chart)
      })
    }
  }

  _chartContainer = React.createRef()

  _setIndicator = indicatorName => {
    // $scope.jszbLists=false
    const tanIndicator = Indis[indicatorName]

    if (this._chart.isIndicator(tanIndicator)) {
      // 是Indecator
      this._removeIndicator(this.props.curIndicators.curInd)
      this._chart.addOrUpdateIndicator(tanIndicator)
      // $scope.curzb = indicatorName;
      this.props.setCurIndicator({curInd: indicatorName})
      if (this.props.pro) saveChartUserSettings(this._chart, indicatorName)
    } else {
      this._removeOver(this.props.curIndicators.curOver)
      if (tanIndicator === 'ema' || tanIndicator === 'ma') {
        this._chart.addOrUpdateIndicator(tanIndicator, 7)
        this._chart.addOrUpdateIndicator(tanIndicator, 25)
        this._chart.addOrUpdateIndicator(tanIndicator, 99)
      } else {
        this._chart.addOrUpdateIndicator(tanIndicator)
      }

      this.props.setCurIndicator({curOver: indicatorName})
      if (this.props.pro) saveChartUserSettings(this._chart)
    }
  }

  _removeIndicator = ind => {
    const indicatorName = Indis[ind]
    if (indicatorName === 'ema' || indicatorName === 'ma') {
      this._chart.removeIndicatorByname([indicatorName])
      this._chart.removeIndicatorByname([indicatorName])
      this._chart.removeIndicatorByname([indicatorName])
    } else {
      this._chart.removeIndicatorByname([indicatorName])
    }
    if (this.props.pro) saveChartUserSettings(this._chart)
  }

  _removeOver = ind => {
    const indicatorName = Indis[ind]
    if (indicatorName === 'ema' || indicatorName === 'ma') {
      this._chart.removeIndicatorByname([indicatorName])
      this._chart.removeIndicatorByname([indicatorName])
      this._chart.removeIndicatorByname([indicatorName])
    } else {
      this._chart.removeIndicatorByname([indicatorName])
    }

    if (this.props.pro) saveChartUserSettings(this._chart)
  }

  render () {
    const {hidden, isDataLoading, height} = this.props

    return (
      <Fragment>
        {/* <LoadingShade hidden={hidden || !isDataLoading} />*/}
        <ChartContainer
          ref={this._chartContainer}
          hidden={hidden}
          fullScreen={this.props.fullScreen}
          selectedView={this.props.selectedView}
          className={'chartContainer'}
          height={height}
        >
          {/* <canvas ref={this._setCanvasRef} /> */}
        </ChartContainer>
      </Fragment>
    )
  }
}
